import { z } from 'zod';
import { messageReactionSummarySchema, messageReplySchema, reactionEmojiSchema } from './reactions';

/**
 * Gesprekken over bedrijven heen (ACC-4, ADR-0082).
 *
 * Een deelnemer is een paar **(bedrijf, persoon)**: dezelfde mens kan in twee bedrijven werken, en
 * het gesprek zegt in welk van de twee hij meedoet. Daarom staat bij elke naam ook de
 * bedrijfsnaam. Zo denkt niemand dat een collega uit het eigen bedrijf meeleest.
 *
 * Buiten naam en bedrijfsnaam komt er niets uit het andere bedrijf mee: geen functie, geen
 * afdeling, geen foto.
 */
export const crossTenantPersonSchema = z.object({
  tenantId: z.guid(),
  personId: z.guid(),
  displayName: z.string(),
  tenantName: z.string(),
});
export type CrossTenantPerson = z.infer<typeof crossTenantPersonSchema>;

/** Wie je kunt vinden: alleen wie een bedrijf met je deelt en het recht heeft. */
export const crossTenantContactsSchema = z.object({
  items: z.array(crossTenantPersonSchema),
});
export type CrossTenantContacts = z.infer<typeof crossTenantContactsSchema>;

export const crossTenantParticipantSchema = crossTenantPersonSchema.extend({
  /** Een van jouw eigen accounts. */
  me: z.boolean(),
});
export type CrossTenantParticipant = z.infer<typeof crossTenantParticipantSchema>;

export const crossTenantConversationSchema = z.object({
  id: z.guid(),
  kind: z.enum(['direct', 'group']),
  /** De groepsnaam, of de naam van de ander. */
  title: z.string(),
  /** De bedrijven van de anderen, voor de badge "Over bedrijven heen". */
  otherTenantNames: z.array(z.string()),
  participants: z.array(crossTenantParticipantSchema),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: z.string().datetime().nullable(),
  lastMessageMine: z.boolean(),
  unreadCount: z.number().int(),
});
export type CrossTenantConversation = z.infer<typeof crossTenantConversationSchema>;

export const crossTenantConversationListSchema = z.object({
  items: z.array(crossTenantConversationSchema),
  totalUnread: z.number().int(),
});
export type CrossTenantConversationList = z.infer<typeof crossTenantConversationListSchema>;

export const crossTenantMessageSchema = z.object({
  id: z.guid(),
  conversationId: z.guid(),
  senderDisplayName: z.string(),
  senderTenantName: z.string(),
  body: z.string(),
  createdAt: z.string().datetime(),
  mine: z.boolean(),
  /** Zoals bij een gewoon gesprek (ADR-0068): alleen onder je eigen bericht in een groep. */
  readByCount: z.number().int().nullable(),
  /** Zoals bij een gewoon gesprek (BER-8): per emoji een aantal, geen namen. */
  reactions: z.array(messageReactionSummarySchema),
  myReaction: reactionEmojiSchema.nullable(),
  /** Het bericht waarop dit een antwoord is, of `null`. */
  replyTo: messageReplySchema.nullable(),
});
export type CrossTenantMessage = z.infer<typeof crossTenantMessageSchema>;

export const crossTenantMessagePageSchema = z.object({
  items: z.array(crossTenantMessageSchema),
  nextCursor: z.string().nullable(),
});
export type CrossTenantMessagePage = z.infer<typeof crossTenantMessagePageSchema>;

export const crossTenantMemberRefSchema = z.object({
  tenantId: z.guid(),
  personId: z.guid(),
});
export type CrossTenantMemberRef = z.infer<typeof crossTenantMemberRefSchema>;

/**
 * Eén ander: een één-op-één-gesprek, idempotent. Meer anderen: een groep, met een naam.
 */
export const startCrossTenantConversationRequestSchema = z
  .object({
    participants: z.array(crossTenantMemberRefSchema).min(1).max(50),
    title: z.string().trim().min(1).max(80).optional(),
  })
  .refine((request) => request.participants.length === 1 || Boolean(request.title), {
    message: 'Een groep heeft een naam nodig.',
    path: ['title'],
  });
export type StartCrossTenantConversationRequest = z.infer<
  typeof startCrossTenantConversationRequestSchema
>;

export const sendCrossTenantMessageRequestSchema = z.object({
  body: z.string().trim().min(1, 'Een bericht mag niet leeg zijn.').max(2000),
  /** Antwoorden op een bericht uit hetzelfde gesprek (BER-8). */
  replyToId: z.guid().nullable().optional(),
});
export type SendCrossTenantMessageRequest = z.infer<typeof sendCrossTenantMessageRequestSchema>;
